import Button from '@/components/Button/Button';
import { ButtonVariants } from '@/components/Button/Button.types';
import Card from '@/components/Card/Card';
import Dropdown from '@/components/Dropdown/Dropdown';
import Header from '@/components/Header/Header';
import Icon from '@/components/Icons/Icon';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useAddAvailablePrice, useEditBundle } from '@/services/bundles/bundles.service';
import { renderOccasions } from '@/utils/formatting/formatBundleProperties';
import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';
import { Chip, Stack, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ChangeEvent, FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import EditLinkedBundlesModal from '../EditLinkedBundlesModal/EditLinkedBundlesModal';
import { IconLinkedBundleText } from '../Internal/IconLinkedBundleText';
import DeleteConfirmationModal from './DeleteConfirmationModal/DeleteConfirmationModal';
import styles from './EditBundle.module.css';
import { EditBundleProps } from './EditBundle.types';
import { LockSettings } from './internal/LockSettings';
import { UpChargePrice } from './internal/UpChargePrice';

const EditBundle: FC<EditBundleProps> = ({ bundleData, setShowEditBundle, bundleId, postDeleteBundleData }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const REC_PRICES_SIZE = 4;

  const { mutate } = useEditBundle(bundleData?.id || '');
  const [priceToDelete, setPriceToDelete] = useState('');
  const [bundleIdToDelete, setBundleIdToDelete] = useState(bundleData?.id || '');

  const occasion = renderOccasions(t, bundleData?.occasions);
  const availablePrices = bundleData?.availablePrices?.map((price) => price?.toFixed(2));
  const recommendedPrices = bundleData?.recommendedPrices?.map((price) => price?.toFixed(2));

  const addAvailablePriceMutation = useAddAvailablePrice(bundleData?.id || '');

  const [prices, setPrices] = useState(bundleData?.availablePrices || []);
  const [newPrice, setNewPrice] = useState<number | null>(null);
  const [availablePricesDupError, setAvailablePricesDupError] = useState('');
  const newPriceRef = useRef<HTMLInputElement | null>(null);
  const [inputPrice, setInputPrice] = useState('');
  const [upChargePrice, setUpChargePrice] = useState(String(bundleData?.upChargePrice));
  const initialUpChargePrice = String(bundleData?.upChargePrice);

  const linkedBundles = bundleData?.bundleLinkedToDto;

  const addNewPrice = async () => {
    setAvailablePricesDupError('');
    const inputValue = newPriceRef.current?.value;

    if (inputValue !== undefined && inputValue.trim() !== '') {
      const newPriceValue = parseFloat(inputValue);

      if (!Number.isNaN(newPriceValue) && prices.includes(newPriceValue)) {
        setAvailablePricesDupError(t('editBundle.prices.duplicatePrice'));
      } else if (inputValue !== undefined && !Number.isNaN(parseFloat(inputValue)) && linkedBundles) {
        await addAvailablePriceMutation.mutateAsync({
          price: parseFloat(inputValue),
        });

        queryClient.invalidateQueries(['bundle', bundleData?.id]);

        const updatedPrices = [...prices, newPriceValue];
        updatedPrices.sort((a, b) => a - b);
        setPrices(updatedPrices);
        setNewPrice(newPriceValue);
        newPriceRef.current!.value = '';

        showToast({
          id: 'successAddingBundlePrice',
          label: t('editBundle.prices.newPriceAdded'),
          toastType: ToastTypes.Success,
        });
      } else if (inputValue !== undefined && !Number.isNaN(parseFloat(inputValue))) {
        await addAvailablePriceMutation.mutateAsync({
          price: parseFloat(inputValue),
        });

        queryClient.invalidateQueries(['bundle', bundleData?.id]);

        const updatedPrices = [...prices, newPriceValue];
        updatedPrices.sort((a, b) => a - b);
        setPrices(updatedPrices);
        setNewPrice(newPriceValue);
        newPriceRef.current!.value = '';

        showToast({
          id: 'successAddingBundlePrice',
          label: t('editBundle.prices.newPriceAdded'),
          toastType: ToastTypes.Success,
        });
      }
    }
  };

  // initial recommended prices dropdown values
  const recPricesInitialFormValues: { [string: string]: string } = {
    pricePoint1: recommendedPrices?.[0] || '',
    pricePoint2: recommendedPrices?.[1] || '',
    pricePoint3: recommendedPrices?.[2] || '',
    pricePoint4: recommendedPrices?.[3] || '',
  };

  const [recPricesFormValues, setRecPricesFormValues] = useState<{ [string: string]: string }>({
    pricePoint1: recommendedPrices?.[0] || '',
    pricePoint2: recommendedPrices?.[1] || '',
    pricePoint3: recommendedPrices?.[2] || '',
    pricePoint4: recommendedPrices?.[3] || '',
  });

  const initialLockSettings = {
    isLockPrice: !!bundleData?.isLockPrice,
    isLockRedeemableOnline: !!bundleData?.isLockRedeemableOnline,
    lockRedeemableOnline: !bundleData?.isLockRedeemableOnline ? false : !!bundleData?.lockRedeemableOnline,
    isLockVisibleOnline: !!bundleData?.isLockVisibleOnline,
    lockVisibleOnline: !bundleData?.isLockVisibleOnline ? false : !!bundleData?.lockVisibleOnline,
  };

  const [lockSettings, setLockSettings] = useState({
    isLockPrice: !!bundleData?.isLockPrice,
    isLockRedeemableOnline: !!bundleData?.isLockRedeemableOnline,
    lockRedeemableOnline: !bundleData?.isLockRedeemableOnline ? false : !!bundleData?.lockRedeemableOnline, // this value is irrelevant if isLockRedeemableOnline is false, so hardcoding to false
    isLockVisibleOnline: !!bundleData?.isLockVisibleOnline,
    lockVisibleOnline: !bundleData?.isLockVisibleOnline ? false : !!bundleData?.lockVisibleOnline, // this value is irrelevant if isLockVisibleOnline is false, so hardcoding to false
  });

  // helps track duplicate recommended prices for the dropdowns
  const [duplicateRecPriceErrors, setDuplicateRecPriceErrors] = useState<{ [string: string]: boolean }>({
    pricePoint1: false,
    pricePoint2: false,
    pricePoint3: false,
    pricePoint4: false,
  });

  const priceOptions = availablePrices?.map((price: string) => ({
    value: Number(price).toFixed(2) || '',
    label: `$${Number(price).toFixed(2)}` || '',
  }));

  const defaultDropdownPriceOptions = [{ value: '', label: t('common.select') }, ...(priceOptions || [])];

  const [noOfRecPricesEntered, setNoOfRecPricesEntered] = useState(recommendedPrices?.length || 0); // no of recommended prices entered in the dropdowns

  useEffect(() => {
    const duplicateRecPriceErrorsNone = {
      pricePoint1: false,
      pricePoint2: false,
      pricePoint3: false,
      pricePoint4: false,
    };
    const count = Object.values(recPricesFormValues).filter((x) => x !== '').length;
    setNoOfRecPricesEntered(count);
    const uniqueRecPricesEntered = new Set(Object.values(recPricesFormValues));
    if (uniqueRecPricesEntered.size === REC_PRICES_SIZE) {
      // resets duplicateRecPriceErrors if there are no duplicates
      setDuplicateRecPriceErrors(duplicateRecPriceErrorsNone);
    }
  }, [recPricesFormValues]);

  const handleDropdownSelect = (event: ChangeEvent<HTMLSelectElement>) => {
    if (Object.values(recPricesFormValues).includes(event.target.value)) {
      setDuplicateRecPriceErrors({ ...duplicateRecPriceErrors, [event.target.id]: true });
    } else {
      setDuplicateRecPriceErrors({ ...duplicateRecPriceErrors, [event.target.id]: false });
    }
    setRecPricesFormValues({
      ...recPricesFormValues,
      [event.target.id]: !event.target.value ? '' : event.target.value,
    });
  };

  const requiredPricesNotSet =
    (availablePrices && availablePrices.length >= 4 && noOfRecPricesEntered !== 4) ||
    (availablePrices && availablePrices.length < 4 && noOfRecPricesEntered < availablePrices.length);

  const dropdownOneRequiresPrice =
    availablePrices && availablePrices.length >= 1 && recPricesFormValues.pricePoint1 === '';
  const dropdownTwoRequiresPrice =
    availablePrices && availablePrices.length >= 2 && recPricesFormValues.pricePoint2 === '';
  const dropdownThreeRequiresPrice =
    availablePrices && availablePrices.length >= 3 && recPricesFormValues.pricePoint3 === '';
  const dropdownFourRequiresPrice =
    availablePrices && availablePrices.length >= 4 && recPricesFormValues.pricePoint4 === '';

  const arraysHaveTheSameElements = (a: Array<string>, b: Array<string>) =>
    a.length === b.length && [...a].every((val) => b.includes(val)) && [...b].every((val) => a.includes(val));
  const objectsHaveTheSameContent = (a: any, b: any) =>
    arraysHaveTheSameElements(Object.keys(a), Object.keys(b)) && [...Object.keys(a)].every((key) => a[key] === b[key]);

  const errorInRecPriceDropdowns = Object.values(duplicateRecPriceErrors).includes(true) || requiredPricesNotSet;
  const noChangeInRecPrices = arraysHaveTheSameElements(
    Object.values(recPricesInitialFormValues),
    Object.values(recPricesFormValues),
  );
  const noChangeInLockSettings = objectsHaveTheSameContent(initialLockSettings, lockSettings);
  const noChangeInUpChargePrice = !bundleData?.linkedBundleName
    ? true
    : initialUpChargePrice === upChargePrice || upChargePrice === '';
  const noChangeInBundleSettings = noChangeInRecPrices && noChangeInUpChargePrice && noChangeInLockSettings;
  const saveButtonDisabled = errorInRecPriceDropdowns || noChangeInBundleSettings;
  const displayMaxNumberPriceReached = prices.length >= 100;
  const duplicateRecPriceText = t('editBundle.prices.recPriceAlreadySelected');
  const enterPriceText = t('editBundle.prices.enterRecPriceLabel');
  const filteredRecommendedPrices = Object.values(recPricesFormValues)
    .filter((price: string) => price !== '' && price !== undefined)
    .map((price: string) => Number(price).toFixed(2));

  useEffect(() => {
    setRecPricesFormValues(recPricesInitialFormValues);
  }, [
    recPricesInitialFormValues.pricePoint1,
    recPricesInitialFormValues.pricePoint2,
    recPricesInitialFormValues.pricePoint3,
    recPricesInitialFormValues.pricePoint4,
  ]);

  const handleSave = () => {
    mutate(
      {
        recommendedPrices: filteredRecommendedPrices,
        ...lockSettings,
        upChargePrice,
      },
      {
        onSuccess: () => {
          showToast({
            id: 'editBundlePricesSuccess',
            label: t('editBundle.prices.pricesUpdatedSuccessfully'),
          });
          setShowEditBundle(false);
          queryClient.invalidateQueries(['bundle', bundleData?.id]);
        },
        onError: () => {
          showToast({
            id: 'errorUpdatingBundlePrices',
            label: t('error.updatingPricePoints'),
            toastType: ToastTypes.Error,
          });
        },
      },
    );
  };

  const removeAvailablePrice = (price: number) => {
    const availablePricesNow = prices.filter((aPrice) => aPrice !== price);
    setPrices(availablePricesNow);
  };
  const [deleteConfirmationModalOpen, setDeleteConfirmationModalOpen] = useState(false);
  const closeDeleteModal = () => setDeleteConfirmationModalOpen(false);

  const handleDelete = (price: string) => {
    setPriceToDelete(price);
    //map through each and delete?
    setBundleIdToDelete(bundleIdToDelete);
    setDeleteConfirmationModalOpen(true);
    setAvailablePricesDupError('');
  };

  const handleInputChange = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (/^[0-9]*\.?[0-9]*$/.test(value)) {
      setInputPrice(value);
    }
  };

  const [isEditLinkedBundlesModalOpen, setIsEditLinkedBundleSettingsModalOpen] = useState<boolean>(false);

  const openLinkedBundleModal = () => {
    setIsEditLinkedBundleSettingsModalOpen(true);
  };

  const handleLinkedBundleClose = () => {
    setIsEditLinkedBundleSettingsModalOpen(false);
  };

  useEffect(() => {
    setIsEditLinkedBundleSettingsModalOpen(false);
  }, [bundleId]);

  return (
    <div data-testid="div-1">
      <Header
        title={
          bundleData?.code !== '-' && bundleData?.name !== '-'
            ? t('editBundle.prices.titleWithCode', { name: `${bundleData?.code} - ${bundleData?.name}` })
            : t('editBundle.prices.title')
        }
        additionalTitleInfo={bundleData?.bundleLinkedToDto ? <IconLinkedBundleText /> : ''}
        onClickAdditional={() => openLinkedBundleModal()}
        breadcrumb={{
          label: t('breadcrumb.backToBundleDetails'),
          to: '',
          onClick: () => setShowEditBundle(false),
        }}
      />

      <EditLinkedBundlesModal
        isOpen={isEditLinkedBundlesModalOpen}
        onClose={handleLinkedBundleClose}
        detail={bundleData?.bundleLinkedToDto || []}
        bundleName={bundleData?.linkedBundleName || ''}
      />
      <div className={styles.page}>
        <div className={styles.bundleDetails}>
          <h2>{t('editBundle.prices.header')}</h2>
          <Card className={styles.row}>
            <div className={styles.bundleDetailsItemsContainer}>
              <div className={styles.bundleDetailsItem}>
                <div className={styles.displayName}>{t('editBundle.prices.displayNameLabel')}:</div>
                <div>{bundleData?.name}</div>
              </div>
              <div className={styles.bundleDetailsItem}>
                <div className={styles.bundleCode}>{t('editBundle.prices.bundleCodeLabel')}:</div>
                <div>{bundleData?.code}</div>
              </div>
              <div className={styles.bundleDetailsItem}>
                <div className={styles.bundleDesc}>{t('editBundle.prices.bundleDescLabel')}:</div>
                <div>{bundleData?.internalName}</div>
              </div>
              <div className={styles.bundleDetailsItem}>
                <div className={styles.prices}>{t('editBundle.prices.pricesLabel')}:</div>
                <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" paddingBottom="10px">
                  {availablePrices?.map((price) => (
                    <Chip
                      data-testid="icon"
                      key={`$${price}`}
                      label={`$${price}`}
                      onDelete={() => handleDelete(`${price}`)}
                      deleteIcon={<CloseIcon style={{ color: 'black', fontSize: 'large' }} />}
                      color="default"
                      variant="outlined"
                      sx={{
                        boxShadow: '4',
                        fontSize: '14px',
                        height: '28px',
                        // eslint-disable-next-line radix
                        ...(newPrice?.toFixed(2) === price
                          ? { backgroundColor: 'var(--green-100)', color: 'black' }
                          : { backgroundColor: 'white', color: 'black' }),
                      }}
                    />
                  )) || '-'}
                </Stack>
              </div>
              <div className={styles.bundleDetailsItem}>
                <div className={styles.newPrice} />
                {displayMaxNumberPriceReached && (
                  <div className={styles.maxPricesError}>
                    <Icon iconName="AlertCircle" className={styles.icon} />
                    <p className={styles.toManyPricesP}>{t('editBundle.prices.maxPricesReached')}</p>
                  </div>
                )}
              </div>
              <div className={styles.bundleDetailsItem}>
                <div className={styles.newPrice} />
                <div hidden={displayMaxNumberPriceReached}>
                  <p className={styles.inputLabel}>{t('editBundle.prices.addNewPriceLabel')}</p>
                  <input
                    type="text"
                    name="text"
                    id="text"
                    className={styles.customInput}
                    placeholder=""
                    pattern="[0-9]*"
                    onChange={handleInputChange}
                    value={inputPrice}
                    ref={newPriceRef}
                  />
                  <button type="button" className={styles.primaryButton} onClick={addNewPrice}>
                    {t('editBundle.prices.addButton')}
                  </button>
                  {availablePricesDupError && <p className={styles.recPricesDupError}>{availablePricesDupError}</p>}
                </div>
              </div>
              <div className={styles.bundleDetailsItem}>
                <div className={styles.occasion}>{t('editBundle.prices.occasionLabel')}:</div>
                <div>{occasion}</div>
              </div>
            </div>
          </Card>
          <Card className={styles.recommendedBundlePrices}>
            <div className={styles.buttonsRow}>
              <div>
                {!bundleData?.bundleLinkedToDto && (
                  <Typography
                    sx={{
                      fontFamily: 'Open Sans',
                      paddingTop: 2,
                      paddingBottom: 2,
                      fontSize: ' 1.8rem',
                    }}
                  >
                    <span className={styles.bundleSettingsLetters}>Bundle Settings</span>
                  </Typography>
                )}
                {bundleData?.bundleLinkedToDto && (
                  <Typography
                    sx={{
                      fontFamily: 'Open Sans',
                      paddingTop: 2,
                      paddingBottom: 2,
                      fontSize: ' 1.8rem',
                    }}
                  >
                    <span className={styles.bundleSettingsLetters}>Bundle Settings</span>
                    <LinkIcon
                      fontSize="large"
                      id="typography"
                      style={{ color: '#266DCC', verticalAlign: 'middle', textAlign: 'center' }}
                    />
                    Linked bundle - Changes will affect all linked bundles.
                  </Typography>
                )}
              </div>
              <div className={styles.buttons}>
                <Button onClick={handleSave} disabled={saveButtonDisabled}>
                  {t('common.save')}
                </Button>
                <Button variant={ButtonVariants.Secondary} onClick={() => setShowEditBundle(false)}>
                  {t('common.cancel')}
                </Button>
              </div>
            </div>

            <div className={styles.buttonsRow}>
              <div className={styles.divider} />
            </div>

            <div className={styles.buttonsRow}>
              <h3 className={styles.title}>Recommended Bundle Prices</h3>
            </div>

            <div className={styles.buttonsRow}>
              <br />
              <br />
            </div>

            <div className={styles.row}>
              <div className={styles.dropdownContainer}>
                <Dropdown
                  label={t('editBundle.prices.pricePoint1')}
                  id="pricePoint1"
                  options={defaultDropdownPriceOptions}
                  redBackground={dropdownOneRequiresPrice}
                  hidePlaceholder={false}
                  disabled={!!(availablePrices && availablePrices.length < 1)}
                  errorText={
                    dropdownOneRequiresPrice
                      ? enterPriceText
                      : duplicateRecPriceErrors.pricePoint1 === false
                      ? undefined
                      : duplicateRecPriceText
                  }
                  value={
                    recPricesInitialFormValues.pricePoint1 !== recPricesFormValues.pricePoint1
                      ? undefined
                      : recPricesInitialFormValues.pricePoint1
                  }
                  onChange={handleDropdownSelect}
                />
              </div>
              <div className={styles.dropdownContainer}>
                <Dropdown
                  label={t('editBundle.prices.pricePoint2')}
                  id="pricePoint2"
                  options={defaultDropdownPriceOptions}
                  redBackground={dropdownTwoRequiresPrice}
                  hidePlaceholder={false}
                  disabled={!!(availablePrices && availablePrices.length < 2)}
                  errorText={
                    dropdownTwoRequiresPrice
                      ? enterPriceText
                      : duplicateRecPriceErrors.pricePoint2 === false
                      ? undefined
                      : duplicateRecPriceText
                  }
                  value={
                    recPricesInitialFormValues.pricePoint2 !== recPricesFormValues.pricePoint2
                      ? undefined
                      : recPricesInitialFormValues.pricePoint2
                  }
                  onChange={handleDropdownSelect}
                />
              </div>
            </div>
            <div className={styles.row}>
              <div className={styles.dropdownContainer}>
                <Dropdown
                  label={t('editBundle.prices.pricePoint3')}
                  id="pricePoint3"
                  options={defaultDropdownPriceOptions}
                  redBackground={dropdownThreeRequiresPrice}
                  hidePlaceholder={false}
                  disabled={!!(availablePrices && availablePrices.length < 3)}
                  errorText={
                    dropdownThreeRequiresPrice
                      ? enterPriceText
                      : duplicateRecPriceErrors.pricePoint3 === false
                      ? undefined
                      : duplicateRecPriceText
                  }
                  value={
                    recPricesInitialFormValues.pricePoint3 !== recPricesFormValues.pricePoint3
                      ? undefined
                      : recPricesInitialFormValues.pricePoint3
                  }
                  onChange={handleDropdownSelect}
                />
              </div>
              <div className={styles.dropdownContainer}>
                <Dropdown
                  label={t('editBundle.prices.pricePoint4')}
                  id="pricePoint4"
                  options={defaultDropdownPriceOptions}
                  redBackground={dropdownFourRequiresPrice}
                  hidePlaceholder={false}
                  disabled={!!(availablePrices && availablePrices.length < 4)}
                  errorText={
                    dropdownFourRequiresPrice
                      ? enterPriceText
                      : duplicateRecPriceErrors.pricePoint4 === false
                      ? undefined
                      : duplicateRecPriceText
                  }
                  value={
                    recPricesInitialFormValues.pricePoint4 !== recPricesFormValues.pricePoint4
                      ? undefined
                      : recPricesInitialFormValues.pricePoint4
                  }
                  onChange={handleDropdownSelect}
                />
              </div>
            </div>
            {bundleData?.bundleLinkedToDto && (
              <>
                <div className={styles.buttonsRow}>
                  <div className={styles.divider} />
                </div>
                <div className={styles.buttonsRow}>
                  <h3 className={styles.title}>Linked Bundle Upcharge</h3>
                </div>

                <UpChargePrice
                  upChargePrice={String(bundleData?.upChargePrice)}
                  onChange={(upChargeValue) => setUpChargePrice(upChargeValue)}
                  data-testid="upChargeInput"
                />
              </>
            )}
            <div className={styles.divider} />
            <LockSettings
              onChange={(values) => {
                setLockSettings({
                  ...values,
                });
              }}
              showLockPriceSwitch={!!bundleData?.linkedBundleName}
              {...lockSettings}
            />
          </Card>
        </div>
      </div>
      <DeleteConfirmationModal
        postDeleteBundleData={postDeleteBundleData}
        bundleId={bundleIdToDelete}
        price={priceToDelete}
        prices={prices}
        setPrices={setPrices}
        availablePrices={availablePrices || []}
        removePrice={(price: number) => removeAvailablePrice(price)}
        isOpen={deleteConfirmationModalOpen}
        onClose={closeDeleteModal}
      />
    </div>
  );
};
export default EditBundle;
